/*
 * @providesModule MergeAccountController
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import AWSManager from 'AWSManager';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import {_m} from 'INTL';
import LoadingIndicator from 'LoadingIndicator';
import {NativeModules, Platform} from 'react-native';
import NavigationManager from 'NavigationManager';
import NetClient from 'NetClient';
import {ErrorResponse} from 'NetClientTypes';
import Prefs from 'Preferences';
import _ from 'lodash';
import React from 'react';
import MergeAccountScreen from 'MergeAccountScreen';
import {UserCardInfo} from '@skillz-rn/user-account/src/MergeAccountCard/MergeAccountCard';
import {SKZUserInterface} from 'SKZUser';
import NotificationToast from 'NotificationToast';
import NotificationNavigationController from 'NotificationNavigationController';
import SKZConstants from '../Utilities/Constants/_SKZConstants';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';
import {
  MERGE_ACCOUNT_AUTO_MERGE,
  MERGE_ACCOUNT_MERGE_CLICK,
  MERGE_ACCOUNT_ON_SHOW,
  MERGE_ACCOUNT_USER_SWAP_CLICK,
} from 'AWSConstants';
import {FTUECredentials} from './AuthenticationController';
import ModalNavigationController from 'ModalNavigationController';
import SkillzRouter from 'SkillzRouter';
import WebDeepLinkHandler from 'WebDeepLinkHandler';
import ScreenNames from 'ScreenNames';
import GlobalEventEmitter from 'GlobalEventEmitter';
import MergePromptModal from 'MergePromptModal';
import RESTStore from 'RESTStore';

const SkillzAPIManager = NativeModules.SKZAPIManager;
const isWeb = Platform.OS === 'web';

interface MergedAccount {
  account_id: number;
  merged_cash_total?: number;
  merged_medals_total?: number;
  merged_ticket_z_total?: number;
  merged_z_total?: number;
  zendesk_ticket_id?: number;
}

interface MergedAccounts {
  account_id: number;
  merged_accounts?: MergedAccount[];
  total_merged_accounts_cash_value?: number;
  zendesk_ticket_id?: number;
}

const MergeAccountController = {
  /**
   * Creates a link to merge the FTUE account with the current user's account.
   * This fixes an edge case where the user quit the app before the merge was complete
   * and returns after 15 minutes when the stored FTUE token has expired.
   */
  async createMergeAccountLink(ftueCredentials: FTUECredentials | null): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      NetClient.postURL(
        `/user/v1/merge-accounts-link`,
        {
          authorization: `Bearer ${ftueCredentials?.authorization}`,
        },
        SkillzAPIManager.OnError.failSilently,
        (error: ErrorResponse | null, _payload?: Object) => {
          if (error) {
            console.error('Merge Account Error: ' + JSON.stringify(error));
            reject(false);
          } else {
            resolve(true);
          }
          return null;
        }
      );
    });
  },

  async forceRefresh(): Promise<void> {
    return new Promise<void>((resolve: () => void) => {
      NativeModules.SKZAPIManager?.refreshAuthToken?.(() => {
        resolve();
      });
    });
  },

  async mergeAccount(
    ftueCredentials: FTUECredentials | null,
    primarySelectedAccount: UserCardInfo,
    currentUser: SKZUserInterface,
    isAutoMerge: boolean = false
  ): Promise<boolean> {
    const selected_account_id =
      primarySelectedAccount.userName === currentUser.username
        ? currentUser.id // no swap
        : ftueCredentials?.skillzUser.id; // player selected FTUE account to be the primary account

    const secondary_account_id = ftueCredentials?.skillzUser.id;

    return new Promise<boolean>((resolve, reject) => {
      NetClient.postURL(
        `/user/v1/merge-accounts`,
        {
          selected_account_id: Number(selected_account_id),
          secondary_account_id: Number(secondary_account_id),
        },
        SkillzAPIManager.OnError.failSilently,
        (error: ErrorResponse | null, _payload?: Object) => {
          const mergeFromUserId =
            primarySelectedAccount.userName === currentUser.username
              ? ftueCredentials?.skillzUser?.id
              : currentUser.id;
          const mergeFromCashBalance =
            primarySelectedAccount.userName === currentUser.username
              ? currentUser.cash_balance ?? 0
              : ftueCredentials?.skillzUser?.cash_balance ?? 0;
          const mergeFromBonusCashBalance =
            primarySelectedAccount.userName === currentUser.username
              ? currentUser.bonus_balance ?? 0
              : ftueCredentials?.skillzUser?.bonus_balance ?? 0;
          const mergePageSwap = primarySelectedAccount.userName !== currentUser.username;

          if (error) {
            console.error('Merge Account Error: ' + JSON.stringify(error));
            trackTelemetryEvent({
              event: TelemetryEventName.MERGE_ACCOUNT,
              payload: {
                merge_account_status: false,
                merge_from_userid: mergeFromUserId,
                merge_from_cash_balance: mergeFromCashBalance,
                merge_from_bonuscash_balance: mergeFromBonusCashBalance,
                merge_page_swap: mergePageSwap,
              },
            });
            this.startAccountMergeErrorSteps(isAutoMerge);
            reject(false);
          } else {
            trackTelemetryEvent({
              event: TelemetryEventName.MERGE_ACCOUNT,
              payload: {
                merge_account_status: true,
                merge_from_userid: mergeFromUserId,
                merge_from_cash_balance: mergeFromCashBalance,
                merge_from_bonuscash_balance: mergeFromBonusCashBalance,
                merge_page_swap: mergePageSwap,
              },
            });
            resolve(true);
          }
        }
      );
    });
  },

  async checkMergeAccountComplete(ftueCredentials: FTUECredentials): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      NetClient.getURL(`/user/v1/merged-accounts`, {}, SkillzAPIManager.OnError.failSilently)
        .then((payload?: MergedAccounts) => {
          if (payload?.merged_accounts?.length) {
            const ftueAccount = payload.merged_accounts.find(
              (account: MergedAccount) =>
                account.account_id.toString() === ftueCredentials.skillzUser.id
            );
            ftueAccount ? resolve(true) : resolve(false);
          } else {
            resolve(false);
          }
        })
        .catch((error: ErrorResponse | null) => {
          console.error('Merge Account Error: ' + JSON.stringify(error));
          reject(false);
        });
    });
  },

  onSuccessAccountMerge(
    success: boolean,
    primarySelectedAccount: UserCardInfo,
    primaryUser: SKZUserInterface,
    ftueCredentials: FTUECredentials,
    isAutoMerge: boolean = false,
    shouldRedirect: boolean = true
  ) {
    if (success) {
      this.removeFTUECredentialsFromLocal(SKZConstants.FTUE_ACCOUNT_TO_MERGE);
      this.removeFTUECredentialsFromLocal(SKZConstants.FTUE_ACCOUNT_TO_MERGE_SMS_ANDROID);
      Prefs.set({[SKZConstants.FTUE_SHOULD_SHOW_ACCOUNT_MERGE]: null});
      Prefs.set({[SKZConstants.FTUE_ACCOUNT_HAS_SEEN_FTUE_TUTORIAL]: null});
      if (primarySelectedAccount.userName !== primaryUser.username) {
        CurrentUserStore.updateUser({
          username: primarySelectedAccount.userName,
        });
      }
      trackTelemetryEvent({
        event: TelemetryEventName.MENU_ACCOUNT_MERGE_ATTEMPTS,
        payload: {
          primary_user_id: primaryUser.id,
          secondary_user_id: ftueCredentials?.skillzUser.id,
          success: 'yes',
        },
      });
    } else {
      trackTelemetryEvent({
        event: TelemetryEventName.MENU_ACCOUNT_MERGE_ATTEMPTS,
        payload: {
          primary_user_id: primaryUser.id,
          secondary_user_id: ftueCredentials?.skillzUser.id,
          success: 'no',
        },
      });
    }

    let maxAttempts = 15;
    const interval = setInterval(() => {
      if (maxAttempts-- <= 0) {
        clearInterval(interval);
        this.startAccountMergeErrorSteps(isAutoMerge);
        return;
      }

      this.checkMergeAccountComplete(ftueCredentials)
        .then(async (mergeComplete: boolean) => {
          if (mergeComplete) {
            this.removeFTUECredentialsFromLocal(SKZConstants.FTUE_ACCOUNT_TO_MERGE);
            this.removeFTUECredentialsFromLocal(SKZConstants.FTUE_ACCOUNT_TO_MERGE_SMS_ANDROID);
            Prefs.set({[SKZConstants.FTUE_SHOULD_SHOW_ACCOUNT_MERGE]: null});
            Prefs.set({[SKZConstants.FTUE_ACCOUNT_HAS_SEEN_FTUE_TUTORIAL]: null});
            clearInterval(interval);
            this.forceRefresh();

            if (isWeb) {
              GlobalEventEmitter.emit('UserSignedIn');
              LoadingIndicator.hide();
              ModalNavigationController.hideModal(
                async () => {
                  if (shouldRedirect) {
                    // check if should pass params
                    const didHandleDeepLink = await WebDeepLinkHandler.checkAndHandleDeepLink();
                    if (didHandleDeepLink) {
                      SkillzRouter.refreshWeb();
                    } else {
                      NavigationManager.popToRoot();
                    }
                  }
                },
                undefined,
                true
              );
            } else {
              LoadingIndicator.hide();
              NavigationManager.pop();
            }

            if (!isAutoMerge) {
              this.displayMergeAccountToast(true);
            }
          }
        })
        .catch(() => {
          clearInterval(interval);
          this.startAccountMergeErrorSteps(isAutoMerge);
          return;
        });
    }, 1000);
  },

  startAccountMergeErrorSteps(isAutoMerge: boolean = false) {
    this.removeFTUECredentialsFromLocal(SKZConstants.FTUE_ACCOUNT_TO_MERGE);
    this.removeFTUECredentialsFromLocal(SKZConstants.FTUE_ACCOUNT_TO_MERGE_SMS_ANDROID);
    Prefs.set({[SKZConstants.FTUE_ACCOUNT_HAS_SEEN_FTUE_TUTORIAL]: null});
    Prefs.set({[SKZConstants.FTUE_SHOULD_SHOW_ACCOUNT_MERGE]: null});
    LoadingIndicator.hide();

    if (isWeb) {
      ModalNavigationController.hideModal(() => {
        if (isAutoMerge) {
          // Jump to home if auto-merge fails
          setTimeout(() => {
            SkillzRouter.backToRoot();
          }, 300);
        }
      });
    } else {
      NavigationManager.pop();
    }

    if (!isAutoMerge) {
      this.displayMergeAccountToast(false);
    }
  },

  async startMergeAccountsFlow(
    ftueCredentials: FTUECredentials | null,
    shouldAutoMerge: boolean = false,
    shouldRedirect: boolean = true,
    showMergePrompt: boolean = false
  ) {
    const newFtueEnabled = ClientPropertyConstants.getFTUEV2FeatureEnabled();
    if (!ClientPropertyConstants.getSMSSaveAccount() || !ftueCredentials) {
      LoadingIndicator.hide();
      return;
    }

    //Wait for home page redirect to be displayed before showing merge account screen
    if (!NavigationManager.isSceneCurrentlyPresented(ScreenNames.ENTRY_SCREEN) && !isWeb) {
      setTimeout(() => {
        this.startMergeAccountsFlow(
          ftueCredentials,
          shouldAutoMerge,
          shouldRedirect,
          showMergePrompt
        );
      }, 1000);
      return;
    }

    const primaryUser = await CurrentUserStore.fetchUser();
    if (
      !ftueCredentials?.skillzUser ||
      primaryUser.username === ftueCredentials?.skillzUser.username
    ) {
      return; // no FTUE user to merge or already merged
    }

    const ftueCredentialsJson = ftueCredentials != null ? JSON.stringify(ftueCredentials) : 'null';
    this.setFtueCredentialsToLocal(SKZConstants.FTUE_ACCOUNT_TO_MERGE, ftueCredentialsJson);
    this.removeFTUECredentialsFromLocal(SKZConstants.FTUE_ACCOUNT_TO_MERGE_SMS_ANDROID);
    Prefs.set({[SKZConstants.FTUE_SHOULD_SHOW_ACCOUNT_MERGE]: true});

    if (showMergePrompt) {
      ModalNavigationController.showModal(
        <MergePromptModal
          onContinue={() => this.startMergeAccountsFlow(ftueCredentials, shouldAutoMerge, false)}
        />,
        {animationType: 'slide', transparent: true}
      );
      return;
    }

    if (shouldAutoMerge) {
      AWSManager.trackEvent(MERGE_ACCOUNT_AUTO_MERGE);
      const primaryAccount = {
        userName: primaryUser.username,
        cashAmount: primaryUser.cash_balance ?? 0,
        profilePic: primaryUser.avatar_url,
      };
      this.attemptToMergeAccounts(
        ftueCredentials,
        primaryAccount,
        primaryUser,
        shouldAutoMerge,
        shouldRedirect
      );
      return;
    }

    const {total_transferable_funds: results} =
      (await this.getTransferableFundsForMerge(primaryUser.id, ftueCredentials?.skillzUser.id)) ||
      {};
    const secondaryFunds = results?.[0];
    const {cash_balance = 0, bonus_balance = 0} = ftueCredentials?.skillzUser || {};
    const mergedUserCash = secondaryFunds
      ? secondaryFunds.transferable_cash + secondaryFunds.transferable_bonus_cash
      : // The merged-in user will only add their cash balance to the primary
        // user's balance; bonus cash will be lost
        cash_balance - bonus_balance;

    // Determine footer text based on merge conditions
    const getFooterText = () => {
      if (secondaryFunds?.transferable_bonus_cash) {
        return "Let's get all your winnings in one place (your primary account).";
      }
      if (newFtueEnabled) {
        return "Let's merge your account balance into your primary account";
      }
      return "Let's merge your balances into your primary account and keep them in one place. You can only merge cash up to your deposit amount.";
    };

    const component = (
      <MergeAccountScreen
        footerText={_m(getFooterText())}
        bodyText={_m('Looks like you already have an account with us.')}
        userAccounts={[
          {
            userName: primaryUser.username,
            profilePic: {
              uri: primaryUser.avatar_url,
            },
            cashAmount: primaryUser.cash_balance ?? 0,
          },
          {
            userName: ftueCredentials?.skillzUser.username ?? '',
            profilePic: {uri: ftueCredentials?.skillzUser.avatar_url},
            cashAmount: mergedUserCash,
          },
        ]}
        mergePress={(primarySelectedAccount: UserCardInfo) => {
          LoadingIndicator.show();
          AWSManager.trackEvent(MERGE_ACCOUNT_MERGE_CLICK);
          this.attemptToMergeAccounts(
            ftueCredentials,
            primarySelectedAccount,
            primaryUser,
            shouldAutoMerge
          );
        }}
        swapAccountsPress={() => AWSManager.trackEvent(MERGE_ACCOUNT_USER_SWAP_CLICK)}
        allowAccountSelection={false}
      />
    );
    AWSManager.trackEvent(MERGE_ACCOUNT_ON_SHOW);

    if (isWeb) {
      ModalNavigationController.showModal(component);
    } else {
      NavigationManager.pushComponentWithScene(component, true, 'MergeAccount');
    }
  },

  attemptToMergeAccounts(
    ftueCredentials: FTUECredentials | null,
    primarySelectedAccount: UserCardInfo,
    currentUser: SKZUserInterface,
    isAutoMerge: boolean = false,
    shouldRedirect: boolean = true
  ) {
    this.mergeAccount(ftueCredentials, primarySelectedAccount, currentUser, isAutoMerge)
      .then((success: boolean) => {
        this.onSuccessAccountMerge(
          success,
          primarySelectedAccount,
          currentUser,
          ftueCredentials,
          isAutoMerge,
          shouldRedirect
        );
      })
      .catch((error: ErrorResponse) => {
        console.error('Merge Account Error: ' + JSON.stringify(error));
        this.startAccountMergeErrorSteps(isAutoMerge);
      });
  },

  displayMergeAccountToast(success: boolean) {
    const toast: React.ReactElement = (
      <NotificationToast
        toastType={'mergeAccount'}
        secondaryText={
          success
            ? _m('Your accounts were successfully merged. Let the games begin!')
            : _m('Something went wrong during account merge. Please reach out to support.')
        }
        icon={success ? 'merge_account_icon.svg' : 'icon_x_no_padding.svg'}
        onDismiss={() => {}}
      />
    );
    NotificationNavigationController.showInAppToast(toast, {lifetime: 5000});
  },

  // Function to get item in AsyncStorage synchronously
  async getFtueCredentialsFromLocal(key: string): Promise<FTUECredentials | null> {
    try {
      const ftueCredentialsJson = await AsyncStorage.getItem(key);
      if (ftueCredentialsJson) {
        return JSON.parse(ftueCredentialsJson);
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  // Function to remove item in AsyncStorage synchronously
  async removeFTUECredentialsFromLocal(key: string): Promise<void> {
    try {
      await AsyncStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing FTUE credentials from local: ' + error);
    }
  },

  // Function to set item in AsyncStorage synchronously
  async setFtueCredentialsToLocal(key: string, value: string): Promise<void> {
    return new Promise((resolve, reject) => {
      AsyncStorage.setItem(key, value)
        .then(() => resolve())
        .catch((error) => reject(error));
    });
  },

  async getTransferableFundsForMerge(
    primaryUserId: string,
    secondaryUserId: string,
    useCache = false
  ): Promise<TransferableMergeFundsResponse> {
    return new Promise(async (resolve, reject) => {
      RESTStore.getResponseWithOptions(
        `/user/v1/merge-account-transferable-funds/${primaryUserId}`,
        {'secondary-account-id': secondaryUserId},
        SkillzAPIManager.OnError.promptRetry,
        {
          useCache,
          callback: (response, error) => {
            if (error) {
              reject(error);
              return;
            }
            resolve(response);
          },
        }
      );
    });
  },
};

interface TransferableMergeFunds {
  transferable_cash: number;
  transferable_bonus_cash: number;
}

interface TransferableMergeFundsResponse {
  total_transferable_funds: TransferableMergeFunds[];
}

export default MergeAccountController;
